import { Button, CircularProgress, Drawer, styled, Typography } from '@ethings/ui-toolkit';
import { Box, ButtonProps, Dialog, IconButton, LinkProps } from '@mui/material';
import { ResponsiveStyleValue } from '@mui/system';
import { captureException } from '@sentry/react';
import { enqueueSnackbar } from 'notistack';
import { cloneElement, FC, ReactElement, Ref, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuthenticated } from '../../auth';
import { useSmallScreen } from '../../hooks';
import { createMicroPortalRoute, createMicroPortalUrl, createParams, getMicroPortalToken, mergeRefs, trackEvent, useMicroPortalCloseListener } from '../../util';
import { BottomNavigationAction } from '../bottom-navigation';
import { Icon } from '../icons';
import { MiddleSpinner } from '../middle-spinner';
import { MicroPortalDialogProps, MicroPortalDrawerProps, MicroPortalDrawerType, MicroPortalLauncherPageActionProps, MicroPortalLauncherProps, MicroPortalMessageType, MicroPortalProps, MicroPortalType } from './micro-portal.types';

const MicroFrame = styled('iframe')({
  border: 'none',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
  display: 'block',
});

const StyledCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  padding: 6,
  zIndex: 500,
  [theme.breakpoints.down('sm')]: {
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
}));

const StyledBox = styled(Box)(({ theme }) => ({
  overflow: 'hidden',
  paddingTop: 0,
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(6),
    minWidth: 'fit-content',
  },
  [theme.breakpoints.up('sm')]: {
    minWidth: '620px',
  },
}));

const StyledDrawer = styled(Drawer)(() => ({
  zIndex: 1350,
}));

export const MicroPortalErrorFallback: FC<{ error?: string }> = ({ error }) => {
  const { t } = useTranslation(['common']);
  return <Box padding={3} textAlign='center'>
    <Typography>{error || t('common:page.sign-in.hint.failure.unauthorized_client')}</Typography>
  </Box>;
};

export const MicroPortalLoadingFallback: FC<{ app: string; message?: string }> = ({ message, app }) => {
  const { t } = useTranslation(['common']);
  return <MiddleSpinner sx={{ flexDirection: 'column', gap: 2, textTransform: 'capitalize' }}>
    {message || t('component.micro-portal-launcher.launching.label', { app })}
  </MiddleSpinner>;
};

const getDrawerAnchor = (type: string): 'left' | 'right' | 'bottom' => {
  switch (type) {
    case 'drawer-left':
      return 'left';
    case 'drawer-right':
      return 'right';
    case 'drawer-bottom':
      return 'bottom';
    default:
      return 'right';
  }
};

const DEFAULT_RESPONSIVE_VALUES = {
  dialog: {
    width: {
      xs: '80vw',
      sm: '500px',
      md: '700px',
      lg: '1000px',
    },
    height: {
      xs: '100vh',
      md: '60vh',
    },
  },
  drawerLeftRight: {
    width: {
      xs: '100vw',
      sm: '80vw',
      md: '60vw',
      lg: '50vw',
      xl: '40vw',
    },
    height: '100vh',
  },
  drawerBottom: {
    height: {
      xs: '100vh',
      md: '60vh',
    },
  },
  default: {
    width: '100%',
    height: '100%',
  },
};

interface MicroPortalBreakpointSettings {
  width?: ResponsiveStyleValue<string> | string;
  height?: ResponsiveStyleValue<string> | string;
}

const getBreakpointSettings = (type: MicroPortalType): MicroPortalBreakpointSettings => {
  switch (type) {
    case 'dialog': {
      return DEFAULT_RESPONSIVE_VALUES.dialog;
    }
    case 'drawer-left':
    case 'drawer-right': {
      return DEFAULT_RESPONSIVE_VALUES.drawerLeftRight;
    }
    case 'drawer-bottom': {
      return DEFAULT_RESPONSIVE_VALUES.drawerBottom;
    }
    default: {
      return DEFAULT_RESPONSIVE_VALUES.default;
    }
  }
};

export const MicroPortal: FC<MicroPortalProps> = ({
  app,
  data = {},
  title = '',
  errorFallback,
  loadingFallback,
  onError,
  onSuccess,
  scrolling = 'no',
  testId,
  token: passedToken,
  devHost,
  iframeRef,
  height,
  autoResize = false,
}) => {
  const { axios, account } = useAuthenticated();
  const [loading, setLoading] = useState<boolean>(true);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [token, setToken] = useState<string | undefined>(passedToken);
  const [iframeHeight, setIframeHeight] = useState<number | undefined>(height);
  const { t, i18n } = useTranslation(['common']);
  const loadingFallbackComponent = loadingFallback || <MicroPortalLoadingFallback app={app} />;
  const localIframeRef = useRef<HTMLIFrameElement>(undefined);
  const iframeRefs = mergeRefs([localIframeRef, iframeRef]);

  useEffect(() => {
    setIframeHeight(height);
  }, [height]);

  useEffect(() => {
    const onMessage = (event: MessageEvent<{
      type?: string;
      height?: number;
      value?: number;
      payload?: { height?: number };
    }>): void => {
      if (!autoResize || !localIframeRef.current || event.source !== localIframeRef.current.contentWindow) {
        return;
      }
      if (event.data.type !== MicroPortalMessageType.RESIZE) {
        return;
      }

      const nextHeight = event.data.height ?? event.data.payload?.height ?? event.data.value;
      if (typeof nextHeight !== 'number' || !Number.isFinite(nextHeight) || nextHeight <= 0) {
        return;
      }

      setIframeHeight(nextHeight);
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [autoResize]);

  useEffect(() => {
    setLoading(true);

    if (passedToken) {
      setToken(passedToken);
      setLoading(false);
      return;
    }

    let mounted = true;

    getMicroPortalToken(axios, app).then((authToken) => {
      setToken(authToken);
      setError(undefined);
      onSuccess?.();
    }).catch((error) => {
      if (!onError) {
        enqueueSnackbar(error instanceof Error ? error.message : t('common:page.sign-in.hint.failure.unauthorized_client'), { variant: 'error' });
      }
      if (!mounted) return;
      setToken(undefined);
      setError((error as Error).message);
      onError?.(error as Error);
      captureException(error);
    }).finally(() => {
      if (!mounted) return;
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [t, axios, app, setToken, setError, setLoading, passedToken, onError, onSuccess]);

  if (error) {
    return errorFallback ? <>{errorFallback(error)}</> : <MicroPortalErrorFallback error={error} />;
  }

  if (loading || !account.homeDomain || !token) {
    return <>{loadingFallbackComponent}</>;
  }

  const src = createMicroPortalUrl(app, createParams({ auth: token, domain: account.homeDomain, language: i18n.language, ...data }), devHost);
  return (
    <>
      {isIframeLoading ? loadingFallbackComponent : null}
      <MicroFrame
        data-testid={testId}
        src={src}
        scrolling={scrolling}
        title={title}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
        allow="geolocation"
        referrerPolicy="no-referrer"
        onLoad={() => { setIsIframeLoading(false); }}
        style={{ display: isIframeLoading ? 'none' : undefined, height: iframeHeight }}
        ref={iframeRefs as Ref<HTMLIFrameElement>}
      />
    </>
  );
};

const StyledLink = styled(Link)(() => ({
  textTransform: 'capitalize',
}));

export const MicroPortalLauncher: FC<MicroPortalLauncherProps> = (props) => {
  return props.isPersistent ? <PersistentMicroPortalLauncher {...props} /> : <NormalMicroPortalLauncher {...props} />;
};

const NormalMicroPortalLauncher: FC<MicroPortalLauncherProps> = ({ app, data = {}, type = 'href', height, width, loadingElement, element, devHost, scrolling, errorFallback, iframeRef, onError, onSuccess, isDismissible }) => {
  const { axios } = useAuthenticated();
  const [token, setToken] = useState<string | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation(['common']);
  const localIframeRef = useRef<HTMLIFrameElement>(undefined);
  const widthSettings = width || getBreakpointSettings(type).width;
  const heightSettings = height || getBreakpointSettings(type).height;
  const iframeRefs = mergeRefs([localIframeRef, iframeRef]);

  const close = useCallback(() => {
    setToken(null);
    onSuccess?.();
  }, [onSuccess]);

  useMicroPortalCloseListener(localIframeRef, close);

  const authenticate = (): void => {
    setLoading(true);

    getMicroPortalToken(axios, app).then((authToken) => {
      setToken(authToken);
    }).catch((error) => {
      if (onError) {
        onError?.(error as Error);
      } else {
        enqueueSnackbar(error instanceof Error ? error.message : t('common:page.sign-in.hint.failure.unauthorized_client'), { variant: 'error' });
      }
    }).finally(() => {
      setLoading(false);
    });
  };

  const params = { ...data };
  if (devHost) {
    params.devHost = devHost;
  }

  if (type === 'href') {
    return element ? cloneElement(element as ReactElement<LinkProps>, { href: createMicroPortalRoute(app, createParams(params)) }) : <StyledLink to={createMicroPortalRoute(app, createParams(params))} target='_blank'>{t('component.micro-portal-launcher.launch.label', { app })}</StyledLink>;
  }

  return (
    <>
      {loading ? loadingElement || <Button disabled startIcon={<CircularProgress color='inherit' size='1rem' />}>{t('component.micro-portal-launcher.launch.label', { app })}</Button> : element ? cloneElement(element as ReactElement<ButtonProps>, { onClick: authenticate }) : <Button onClick={authenticate} >{t('component.micro-portal-launcher.launch.label', { app })}</Button>}
      {type === 'dialog' ? (
        <MicroPortalDialog isOpen={!!token} height={heightSettings} width={widthSettings} onClose={close} isDismissible={isDismissible}>
          {token ? <MicroPortal app={app} data={data} token={token} devHost={devHost} scrolling={scrolling} errorFallback={errorFallback} iframeRef={iframeRefs} onSuccess={onSuccess} /> : <></>}
        </MicroPortalDialog>
      ) : <></>}
      {type.includes('drawer-') ? (
        <MicroPortalDrawer
          closeType="close-button"
          width={widthSettings}
          height={heightSettings}
          onClose={close}
          isOpen={!!token}
          type={type as MicroPortalDrawerType}
        >
          {token ? <MicroPortal app={app} data={data} token={token} devHost={devHost} scrolling={scrolling} errorFallback={errorFallback} iframeRef={iframeRefs} onSuccess={onSuccess} /> : <></>}
        </MicroPortalDrawer>
      ) : <></>}
    </>
  );
};

const PersistentMicroPortalLauncher: FC<MicroPortalLauncherProps> = ({ app, data = {}, type = 'drawer-right', height, width, element, devHost, scrolling, errorFallback, iframeRef }) => {
  const { t } = useTranslation(['common']);
  const [isOpen, setIsOpen] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading');
  const localIframeRef = useRef<HTMLIFrameElement>(undefined);
  const widthSettings = width || getBreakpointSettings(type).width;
  const heightSettings = height || getBreakpointSettings(type).height;

  const open = useCallback(() => {
    setIsActivated(true);
    setIsOpen(true);
    trackEvent('micro_portal', 'open', app);
  }, [app]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSuccess = useCallback(() => {
    setStatus('success');
  }, []);

  const handleError = useCallback(() => {
    setStatus('error');
  }, []);

  useMicroPortalCloseListener(localIframeRef, close);

  if (type === 'href') {
    console.warn('Persistent micro portal cannot have \'href\' type.');
    return null;
  }

  const microPortal = (
    <MicroPortal
      app={app}
      data={data}
      errorFallback={errorFallback}
      devHost={devHost}
      onSuccess={handleSuccess}
      onError={handleError}
      scrolling={scrolling}
      iframeRef={mergeRefs([localIframeRef, iframeRef])}
    />
  );

  return (
    <>
      {element ?
        cloneElement(element as ReactElement<ButtonProps>, { onClick: open }) :
        <Button onClick={open}>
          {t('component.micro-portal-launcher.launch.label', { app })}
        </Button>
      }
      {type === 'dialog' && (
        <MicroPortalDialog
          width={widthSettings}
          height={heightSettings}
          keepMounted={isActivated && status === 'success'}
          onClose={close}
          isOpen={isOpen}
        >
          {microPortal}
        </MicroPortalDialog>
      )}
      {(type === 'drawer-right' || type === 'drawer-bottom' || type === 'drawer-left') && (
        <MicroPortalDrawer
          type={type}
          closeType="outside-click"
          width={widthSettings}
          height={heightSettings}
          keepMounted={isActivated && status === 'success'}
          onClose={close}
          isOpen={isOpen}
        >
          {microPortal}
        </MicroPortalDrawer>
      )}
    </>
  );
};

const MicroPortalDrawer: FC<MicroPortalDrawerProps> = ({
  closeType,
  children,
  height,
  isOpen,
  keepMounted,
  onClose,
  type,
  width,
}) => {
  const isSmallScreen = useSmallScreen();

  return (
    <StyledDrawer
      anchor={getDrawerAnchor(type)}
      open={isOpen}
      onClose={closeType === 'outside-click' ? onClose : undefined}
      keepMounted={keepMounted}
    >
      {(closeType === 'close-button' || isSmallScreen) && (
        <StyledCloseButton size="large" onClick={onClose}>
          <Icon icon="CloseIcon" />
        </StyledCloseButton>
      )}
      <StyledBox width={width} height={height}>
        {children}
      </StyledBox>
    </StyledDrawer>
  );
};

const MicroPortalDialog: FC<MicroPortalDialogProps> = ({
  children,
  height,
  isOpen,
  isDismissible,
  keepMounted,
  onClose,
  width,
}) => {
  return (
    <Dialog onClose={isDismissible ? onClose : undefined} open={isOpen} maxWidth="xl" keepMounted={keepMounted}>
      <StyledCloseButton size='large' onClick={onClose}><Icon icon="CloseIcon" /></StyledCloseButton>
      <Box height={height} width={width} overflow="hidden">
        {children}
      </Box>
    </Dialog>
  );
};

const MicroPortalStyledButton = styled(Button)({
  whiteSpace: 'nowrap',
});

export const MicroPortalLauncherPageAction: FC<MicroPortalLauncherPageActionProps> = ({ icon, label, testId, launcherProps }) => {
  const isSmallScreen = useSmallScreen();

  const element = isSmallScreen ?
    <BottomNavigationAction
      data-testid={testId}
      icon={icon}
      label={label}
    /> :
    <MicroPortalStyledButton
      data-testid={testId}
      startIcon={icon}
    >
      {label}
    </MicroPortalStyledButton>;

  return (
    <MicroPortalLauncher
      {...launcherProps}
      element={element}
    />
  );
};
